import { ReactNode, isValidElement } from 'react';
import { P, S, T } from '../../../styles/tokens';

type HintKind = 'cream' | 'warn' | 'success';

interface HintBarProps {
  children: ReactNode;
  /** Visual flavour. Defaults to 'cream'. */
  kind?: HintKind;
  /** Optional leading icon element. When set, renders an [icon | text] row. */
  icon?: ReactNode;
}

const BG: Record<HintKind, string> = {
  cream:   P.cream,
  warn:    '#FFF6E5',
  success: '#EFF7E9',
};

/**
 * Warm hint card. Text children render as a single text node (so getByText /
 * interpolated reward strings stay matchable). Pass an explicit `icon` prop for
 * the [icon | text] layout — never infer the icon from child count (interpolated
 * text like `Кешбэк {x} начислится` is 3 children and must NOT be split).
 */
export function HintBar({ children, kind = 'cream', icon }: HintBarProps) {
  const base = {
    background: BG[kind],
    padding: '10px 12px',
    borderRadius: 12,
  } as const;

  if (icon && isValidElement(icon)) {
    return (
      <div style={{ ...base, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ height: 19, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ flex: 1, font: T.footnote, color: S.label, lineHeight: '19px' }}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...base, font: T.footnote, color: S.label, lineHeight: 1.45 }}>
      {children}
    </div>
  );
}
